import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Search, MapPin, Star, Clock } from "lucide-react";
import { useProviders } from "@/hooks/useProviders";

interface SearchModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const SearchModal = ({ open, onOpenChange }: SearchModalProps) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSpecialty, setSelectedSpecialty] = useState("");
  const { data: providers, isLoading } = useProviders();

  const specialties = [
    "Ice Bath",
    "Sauna", 
    "Cryotherapy",
    "Red Light Therapy",
    "Massage Therapy",
    "Float Tanks",
    "Compression Therapy"
  ];

  // Filter providers based on search query and specialty
  const filteredProviders = providers?.filter(provider => {
    const matchesSearch = searchQuery === "" || 
      provider.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      provider.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      provider.location.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesSpecialty = selectedSpecialty === "" || 
      provider.specialties.some(specialty => 
        specialty.toLowerCase().includes(selectedSpecialty.toLowerCase())
      );
    
    return matchesSearch && matchesSpecialty;
  }) || [];

  const handleProviderSelect = (providerSlug: string) => {
    navigate(`/provider/${providerSlug}`);
    onOpenChange(false);
  };

  const clearFilters = () => {
    setSearchQuery("");
    setSelectedSpecialty("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl h-[90vh] flex flex-col p-0 gap-0">
        <DialogHeader className="flex-shrink-0 p-6 pb-4 border-b">
          <DialogTitle>Search Providers</DialogTitle>
        </DialogHeader>
        
        <div className="flex-1 flex flex-col min-h-0 p-6 pt-4">
          {/* Search Input */}
          <div className="relative flex-shrink-0 mb-4">
            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search by provider name, service, or location..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Specialty Filter */}
          <div className="space-y-2 flex-shrink-0 mb-4">
            <p className="text-sm font-medium">Filter by Specialty:</p>
            <div className="flex flex-wrap gap-2">
              <Button
                variant={selectedSpecialty === "" ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedSpecialty("")}
              >
                All
              </Button>
              {specialties.map((specialty) => (
                <Button
                  key={specialty}
                  variant={selectedSpecialty === specialty ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedSpecialty(specialty)}
                >
                  {specialty}
                </Button>
              ))}
            </div>
          </div>

          {/* Clear Filters */}
          {(searchQuery || selectedSpecialty) && (
            <div className="flex justify-between items-center flex-shrink-0 mb-4">
              <p className="text-sm text-gray-600">
                {filteredProviders.length} provider(s) found
              </p>
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                Clear filters
              </Button>
            </div>
          )}

          {/* Results - Native scrolling with iOS optimization */}
          <div 
            className="flex-1 overflow-y-auto"
            style={{
              WebkitOverflowScrolling: 'touch'
            } as React.CSSProperties}
          >
            <div className="space-y-3">
              {isLoading ? (
                <div className="text-center py-8">
                  <p className="text-gray-500">Searching providers...</p>
                </div>
              ) : filteredProviders.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-500">No providers found matching your criteria</p>
                  <Button variant="ghost" size="sm" onClick={clearFilters} className="mt-2">
                    Clear filters to see all providers
                  </Button>
                </div>
              ) : (
                filteredProviders.map((provider) => (
                  <Card 
                    key={provider.id} 
                    className="cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => handleProviderSelect(provider.slug)}
                  >
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <h3 className="font-semibold text-lg">{provider.name}</h3>
                            <div className="flex items-center space-x-1">
                              <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                              <span className="text-sm font-medium">{provider.rating}</span>
                              <span className="text-sm text-gray-500">({provider.review_count})</span>
                            </div>
                          </div>
                          
                          <p className="text-gray-600 text-sm mb-2 line-clamp-2">
                            {provider.description}
                          </p>
                          
                          <div className="flex items-center space-x-1 text-sm text-gray-500 mb-2">
                            <MapPin className="h-4 w-4" />
                            <span>{provider.location}</span>
                          </div>
                          
                          <div className="flex flex-wrap gap-1">
                            {provider.specialties.slice(0, 3).map((specialty) => (
                              <Badge key={specialty} variant="secondary" className="text-xs">
                                {specialty}
                              </Badge>
                            ))}
                            {provider.specialties.length > 3 && (
                              <Badge variant="outline" className="text-xs">
                                +{provider.specialties.length - 3} more
                              </Badge>
                            )}
                          </div>
                        </div>
                        
                        {provider.image_url && (
                          <img
                            src={provider.image_url}
                            alt={provider.name}
                            className="w-20 h-20 object-cover rounded-lg ml-4"
                          />
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SearchModal;
